import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const MapoTofu: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Classic Mapo Tofu Recipe | Chinese Food Maps</title>
        <meta name="description" content="Learn how to make authentic Sichuan Mapo Tofu at home. Our recipe guides you through creating this spicy, numbing, and comforting dish step by step." />
        <meta property="og:title" content="Classic Mapo Tofu Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to make authentic Sichuan Mapo Tofu at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/mapo-tofu" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/mapo-tofu" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Classic Mapo Tofu Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Mapo Tofu (麻婆豆腐) is a beloved Sichuan dish that combines soft tofu with spicy ground pork in a sauce that's both numbing and spicy. The name literally means "Pockmarked Grandmother's Tofu," named after the old lady who invented this dish.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Ingredients:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 block (400g) soft tofu, cut into 1-inch cubes</li>
              <li>200g ground pork</li>
              <li>2 tbsp Sichuan doubanjiang (spicy bean paste)</li>
              <li>1 tbsp douchi (fermented black beans), chopped</li>
              <li>3 cloves garlic, minced</li>
              <li>1-inch ginger, minced</li>
              <li>2 scallions, chopped</li>
              <li>1 tbsp Sichuan peppercorns, ground</li>
              <li>2 tbsp vegetable oil</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Sauce:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 cup chicken stock</li>
              <li>1 tbsp light soy sauce</li>
              <li>1 tsp dark soy sauce</li>
              <li>2 tsp cornstarch mixed with 2 tbsp water</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Prepare Tofu:</strong> Gently place tofu cubes in salted hot water for 2-3 minutes. This helps firm them up.
              </li>
              <li>
                <strong>Cook Aromatics:</strong> Heat oil in a wok. Fry doubanjiang until oil turns red. Add ground pork and cook until crumbly.
              </li>
              <li>
                <strong>Add Flavoring:</strong> Stir in black beans, garlic, and ginger. Cook until fragrant.
              </li>
              <li>
                <strong>Add Liquid:</strong> Pour in chicken stock and soy sauces. Bring to a simmer.
              </li>
              <li>
                <strong>Cook Tofu:</strong> Carefully add tofu cubes. Simmer for 3-4 minutes until heated through.
              </li>
              <li>
                <strong>Thicken:</strong> Add cornstarch mixture. Stir gently until sauce thickens.
              </li>
              <li>
                <strong>Finish:</strong> Sprinkle with ground Sichuan peppercorns and scallions.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use silken tofu for authentic texture</li>
                <li>Don't skip the doubanjiang - it's essential for authentic flavor</li>
                <li>Handle tofu gently to prevent breaking</li>
                <li>Adjust spiciness by varying the amount of doubanjiang</li>
                <li>Serve immediately while the sauce is hot and silky</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};